// Admin "P&L" tab — revenue from Stripe, expenses you log, and an estimate of
// what clipping cost, per month. Backend: routes/adminFinance.js.
import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import "./finance.css";

const money = (n) =>
  (n < 0 ? "-$" : "$") + Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const monthInput = (d) => d.toISOString().slice(0, 7);
// Local calendar date — toISOString() is UTC, which is already tomorrow on a US evening.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const monthLabel = (k) =>
  new Date(`${k}-01T00:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

function defaultRange() {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 2, 1));
  return { from: monthInput(from), to: monthInput(now) };
}

const EMPTY_FORM = { date: today(), vendor: "", category: "Software", amount: "", monthly: false, endDate: "", notes: "" };

async function download(path, params, fallbackName) {
  try {
    const r = await axiosInstance.get(path, { params, responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || fallbackName;
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Export failed");
  }
}

export default function FinanceTab() {
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [costs, setCosts] = useState(null);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [lifetime, setLifetime] = useState(null);
  const [showAllLifetime, setShowAllLifetime] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get("/admin/finance/pnl", { params: range });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to load P&L");
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCosts(null);
    axiosInstance.get("/admin/finance/costs", { params: range })
      .then((r) => setCosts(r.data))
      .catch((e) => toast.error(e.response?.data?.error || "Failed to load costs"));
  }, [range]);

  useEffect(() => {
    axiosInstance.get("/admin/finance/lifetime")
      .then((r) => setLifetime(r.data))
      .catch((e) => toast.error(e.response?.data?.error || "Failed to load lifetime profit"));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await axiosInstance.put(`/admin/finance/expenses/${editingId}`, form);
      else await axiosInstance.post("/admin/finance/expenses", form);
      toast.success(editingId ? "Expense updated" : "Expense added");
      setForm({ ...EMPTY_FORM, date: form.date });
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Couldn't save expense");
    } finally {
      setSaving(false);
    }
  };

  const edit = (x) => {
    setEditingId(x._id);
    setForm({
      date: x.date.slice(0, 10), vendor: x.vendor, category: x.category, amount: String(x.amount),
      monthly: !!x.monthly, endDate: x.endDate ? x.endDate.slice(0, 10) : "", notes: x.notes || "",
    });
    window.scrollTo({ top: document.querySelector(".fin-form")?.offsetTop - 90 || 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (confirmDelete !== id) return setConfirmDelete(id);
    setConfirmDelete(null);
    try {
      await axiosInstance.delete(`/admin/finance/expenses/${id}`);
      toast.success("Expense deleted");
      if (editingId === id) { setEditingId(null); setForm(EMPTY_FORM); }
      load();
    } catch {
      toast.error("Couldn't delete expense");
    }
  };

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;
  const { months, totals, costPerStreamHour, categories, expenses, revenueError } = data;

  return (
    <div className="admindash-section admindash-fade-in fin">
      <div className="fin-toolbar">
        <label>From <input type="month" value={range.from} onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))} /></label>
        <label>To <input type="month" value={range.to} onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))} /></label>
        <div className="fin-toolbar-spacer" />
        <button className="admindash-refresh" onClick={() => download("/admin/finance/pnl.csv", range, "clipbait-pnl.csv")}>Export P&L CSV</button>
        <button className="admindash-refresh" onClick={() => download("/admin/finance/expenses.csv", range, "clipbait-expenses.csv")}>Export expenses CSV</button>
        <button className="admindash-refresh" onClick={() => download("/admin/finance/costs.csv", range, "clipbait-costs.csv")}>Export costs CSV</button>
      </div>

      {revenueError && <div className="admindash-note">Revenue unavailable: {revenueError}</div>}

      <div className="admindash-grid">
        <div className="admindash-stat"><div className="admindash-stat-value">{money(totals.netRevenue)}</div><div className="admindash-stat-label">Net revenue (after refunds + Stripe fees)</div></div>
        <div className="admindash-stat"><div className="admindash-stat-value">{money(totals.totalCosts)}</div><div className="admindash-stat-label">Total costs</div></div>
        <div className={`admindash-stat ${totals.profit >= 0 ? "admindash-stat--green" : "fin-stat--loss"}`}><div className="admindash-stat-value">{money(totals.profit)}</div><div className="admindash-stat-label">{totals.profit >= 0 ? "Profit" : "Loss"}</div></div>
        <div className="admindash-stat"><div className="admindash-stat-value">{money(totals.computeEstimate)}</div><div className="admindash-stat-label">Clipping cost (estimated)</div></div>
      </div>

      <h3 className="admindash-section-title">By month</h3>
      <table className="admindash-table fin-table">
        <thead>
          <tr><th>Month</th><th>Gross revenue</th><th>Net revenue</th><th>Expenses</th><th>Clipping (est.)</th><th>Profit</th></tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <td>{monthLabel(m.month)}</td>
              <td>{money(m.revenue.gross)}</td>
              <td>{money(m.revenue.net)}</td>
              <td>{money(m.expenses.total)}</td>
              <td title={`${m.streamHours} stream hours × ${money(costPerStreamHour)}`}>
                {m.computeIncluded ? money(m.computeEstimate) : <span className="muted">AI compute logged</span>}
              </td>
              <td className={m.profit >= 0 ? "fin-pos" : "fin-neg"}>{money(m.profit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="admindash-note">
        Clipping cost = stream hours clipped × {money(costPerStreamHour)}/hour (Gemini, Modal, AssemblyAI, storage — measured Oct 2026).
        Log an <b>AI compute</b> expense for a month to use the real invoice instead.
      </div>

      <h3 className="admindash-section-title">Lifetime profit & loss by user</h3>
      {!lifetime ? (
        <div className="admindash-empty admindash-shimmer">Loading…</div>
      ) : (
        <>
          <div className="admindash-grid fin-lifetime-stats">
            <div className="admindash-stat"><div className="admindash-stat-value">{money(lifetime.totals.paid)}</div><div className="admindash-stat-label">Paid, all time</div></div>
            <div className="admindash-stat"><div className="admindash-stat-value">{money(lifetime.totals.cost)}</div><div className="admindash-stat-label">Credits used × cost</div></div>
            <div className={`admindash-stat ${lifetime.totals.profit >= 0 ? "admindash-stat--green" : "fin-stat--loss"}`}><div className="admindash-stat-value">{money(lifetime.totals.profit)}</div><div className="admindash-stat-label">{lifetime.totals.profit >= 0 ? "Profit" : "Loss"}</div></div>
            <div className="admindash-stat"><div className="admindash-stat-value">{lifetime.totals.losing}</div><div className="admindash-stat-label">Users losing money</div></div>
          </div>
          <table className="admindash-table fin-table">
            <thead>
              <tr><th>User</th><th>Plan</th><th>Country</th><th>Minutes clipped</th><th>Credits used</th><th>Cost</th><th>Paid</th><th>Profit / loss</th></tr>
            </thead>
            <tbody>
              {(showAllLifetime ? lifetime.users : lifetime.users.slice(0, 25)).map((u) => (
                <tr key={u.userId}>
                  <td><a className="fin-link" href={`/admin/users/${u.userId}`}>{u.username || "(no name)"}</a> <span className="muted">{u.email}</span></td>
                  <td>{u.plan || "—"}</td>
                  <td>{u.country || "—"}</td>
                  <td>{u.minutesClipped.toLocaleString()}</td>
                  <td>{u.creditsUsed.toLocaleString()} <span className="muted">of {u.creditsGranted.toLocaleString()}</span></td>
                  <td>{money(u.cost)}</td>
                  <td>{money(u.paid)}</td>
                  <td className={u.profit >= 0 ? "fin-pos" : "fin-neg"}>{money(u.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="fin-form-actions">
            {lifetime.users.length > 25 && (
              <button className="admindash-refresh fin-more" onClick={() => setShowAllLifetime((v) => !v)}>
                {showAllLifetime ? "Show fewer" : `Show all ${lifetime.users.length} users`}
              </button>
            )}
            <button className="admindash-refresh fin-more" onClick={() => download("/admin/finance/lifetime.csv", {}, "clipbait-lifetime-profit-by-user.csv")}>Export CSV</button>
          </div>
          <div className="admindash-note">
            Cost = minutes clipped × ${lifetime.costPerCredit}. Minutes clipped is the larger of credits used and the minutes in their
            jobs (some jobs ran past a user's balance before credit holds), and counts jobs users later deleted. Paid = everything their Stripe customer paid, after refunds,
            before fees. Worst losses first.
          </div>
        </>
      )}

      <h3 className="admindash-section-title">Profit & loss by user</h3>
      {!costs ? (
        <div className="admindash-empty admindash-shimmer">Loading…</div>
      ) : costs.users.length === 0 ? (
        <div className="admindash-empty">No clipping or payments in these months.</div>
      ) : (
        <>
          <table className="admindash-table fin-table">
            <thead>
              <tr><th>User</th><th>Plan</th><th>Jobs</th><th>Hours clipped</th><th>Clipping cost</th><th>Paid</th><th>Profit / loss</th></tr>
            </thead>
            <tbody>
              {(showAllUsers ? costs.users : costs.users.slice(0, 25)).map((u) => (
                <tr key={u.userId}>
                  <td><a className="fin-link" href={`/admin/users/${u.userId}`}>{u.username}</a> <span className="muted">{u.email}</span></td>
                  <td>{u.plan || "—"}</td>
                  <td>{u.jobs}</td>
                  <td>{u.hours}</td>
                  <td>{money(u.cost)}</td>
                  <td>{money(u.paid)}</td>
                  <td className={u.profit >= 0 ? "fin-pos" : "fin-neg"}>{money(u.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {costs.users.length > 25 && (
            <button className="admindash-refresh fin-more" onClick={() => setShowAllUsers((v) => !v)}>
              {showAllUsers ? "Show fewer" : `Show all ${costs.users.length} users`}
            </button>
          )}
          <div className="admindash-note">
            Paid = what the user's Stripe customer paid in these months, after refunds. Worst losses first.
            {costs.unlinkedPaid > 0 && <> {money(costs.unlinkedPaid)} came from Stripe customers not linked to a Clipbait account (one-off invoices and the like) and isn't counted here.</>}
          </div>
        </>
      )}

      <h3 className="admindash-section-title">Cost by stream / VOD</h3>
      {costs && costs.jobs.length > 0 && (
        <>
          <table className="admindash-table fin-table">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Streamer</th><th>Video</th><th>User</th><th>Hours</th><th>Cost</th><th>Clips</th></tr>
            </thead>
            <tbody>
              {(showAllJobs ? costs.jobs : costs.jobs.slice(0, 25)).map((r) => (
                <tr key={`${r.kind}-${r.id}`}>
                  <td>{String(r.date).slice(0, 10)}</td>
                  <td>{r.kind}{r.free && <span className="fin-badge">free</span>}</td>
                  <td>{r.streamer || "—"}</td>
                  <td className="fin-title">{r.url ? <a className="fin-link" href={r.url} target="_blank" rel="noreferrer">{r.title || r.url}</a> : r.title || "—"}</td>
                  <td><a className="fin-link" href={`/admin/users/${r.userId}`}>{r.username}</a></td>
                  <td>{r.hours}</td>
                  <td>{money(r.cost)}</td>
                  <td>{r.clips}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {costs.jobs.length > 25 && (
            <button className="admindash-refresh fin-more" onClick={() => setShowAllJobs((v) => !v)}>
              {showAllJobs ? "Show fewer" : `Show all ${costs.jobs.length}`}
            </button>
          )}
          <div className="admindash-note">
            Cost = hours clipped × {money(costPerStreamHour)}/hour. Completed jobs and live sessions only — failed jobs also use some compute that isn't measured here.
          </div>
        </>
      )}

      <h3 className="admindash-section-title">{editingId ? "Edit expense" : "Add expense"}</h3>
      <form className="fin-form" onSubmit={save}>
        <label>Date<input type="date" value={form.date} onChange={set("date")} required /></label>
        <label>Vendor<input value={form.vendor} onChange={set("vendor")} placeholder="Render" required maxLength={120} /></label>
        <label>Category
          <select value={form.category} onChange={set("category")}>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label>Amount ($)<input type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} placeholder="25.00" required /></label>
        <label className="fin-check"><input type="checkbox" checked={form.monthly} onChange={set("monthly")} /> Repeats monthly</label>
        {form.monthly && <label>Ends (optional)<input type="date" value={form.endDate} onChange={set("endDate")} /></label>}
        <label className="fin-notes">Notes<input value={form.notes} onChange={set("notes")} maxLength={500} placeholder="Starter plan" /></label>
        <div className="fin-form-actions">
          <button type="submit" className="admindash-refresh fin-primary" disabled={saving}>{saving ? "Saving…" : editingId ? "Save changes" : "Add expense"}</button>
          {editingId && <button type="button" className="admindash-refresh" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}>Cancel</button>}
        </div>
      </form>

      <h3 className="admindash-section-title">Expenses in this range ({expenses.length})</h3>
      {expenses.length === 0 ? (
        <div className="admindash-empty">No expenses logged for these months yet.</div>
      ) : (
        <table className="admindash-table fin-table">
          <thead>
            <tr><th>Date</th><th>Vendor</th><th>Category</th><th>Amount</th><th>Notes</th><th /></tr>
          </thead>
          <tbody>
            {expenses.map((x) => (
              <tr key={x._id}>
                <td>{x.date.slice(0, 10)}</td>
                <td>{x.vendor}</td>
                <td>{x.category}</td>
                <td>{money(x.amount)}{x.monthly ? <span className="muted"> /mo{x.endDate ? ` until ${x.endDate.slice(0, 10)}` : ""}</span> : null}</td>
                <td className="muted">{x.notes}</td>
                <td className="fin-row-actions">
                  <button onClick={() => edit(x)}>Edit</button>
                  <button className={confirmDelete === x._id ? "fin-danger" : ""} onClick={() => remove(x._id)}>
                    {confirmDelete === x._id ? "Confirm delete" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
